import { useState, useLayoutEffect } from 'react';
import { ThemeContext } from '../../constants/themes';
import { useHistory } from "react-router-dom";
import Button from '../../components/atoms/Button';
import Type from '../../components/atoms/Type';
import Card from '../../components/atoms/Card';
import Navbar from '../../components/molecules/Navbar';
import TextSubmit from '../../components/molecules/TextSubmit';
import Modal from "../../components/molecules/Modal";
import FileUpload from '../../components/molecules/FileUpload';
import YourRecordingRequestRow from './YourRecordingRequestRow';
import ClaimedRecordingRequestRow from './ClaimedRecordingRequestRow';
import OpenRecordingRequestRow from './OpenRecordingRequestRow';
import { withAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import Parrot from '../../api/parrot';
import Stripe from '../../api/stripe';
import { loadStripe } from '@stripe/stripe-js';

import './index.css';
import Loader from '../../components/atoms/Loader';

const parrot = new Parrot();
const internalStripeApi = new Stripe();

const DashboardLoadingState = () => (
  <ThemeContext.Consumer>
    {themeContext => (
      <div className="background" style={{ backgroundColor: themeContext.theme.body }}>
        <Navbar authed={true} />
        <div className="dashboard-content-div">
          <br />
          <div className="modal-loader-div">
            <Loader />
          </div>
        </div>
      </div>
    )}
  </ThemeContext.Consumer>
);

const Dashboard = (props) => {
  let history = useHistory();

  const [state, setState] = useState({
    tab: 'listening',
    fetchingDashboardData: true,
    credits: 0,
    dollars: 0,
    isListener: false,
    isNarrator: false,
    yourPreviousNarrations: [],
    yourRecordingRequests: [],
    claimedRequestedRecordings: [],
    openRequestedRecordings: [],
    otherRequestsFetching: true,
    rssLink: '',
    hasActiveMonthlyStandardSub: false,
    pendingRecordingRequestError: false,
    pendingRecordingRequest: undefined,
    claimingRequest: undefined,
    uploadingRecordingId: undefined,
    showModals: {
      showConfirmArticle: false,
      showGetMoreCredits: false,
      showCashOut: false,
      showConfirmRequestClaim: false,
      showUploadRecording: false
    }
  });

  const partitionArray = (array, filter) => {
    let pass = [], fail = [];
    array.forEach((e, idx, arr) => (filter(e, idx, arr) ? pass : fail).push(e));
    return [pass, fail];
  }

  const fetchDashboardData = async (newStateData, nukeModals = true) => {
    try {
      const accessToken = await props.auth0.getAccessTokenSilently();
      parrot.getDashboardData(accessToken).then(res => {
        if (res) {
          if (!res.isListener && !res.isNarrator) {
            history.push("/");
          }
          const yourPreviousNarrations = res.narrated?.reverse();
          const yourRecordingRequests = res.requests?.reverse();
          setState({
            ...state,
            ...newStateData,
            fetchingDashboardData: false,
            credits: res.credits,
            dollars: res.dollars,
            isListener: res.isListener,
            isNarrator: res.isNarrator,
            yourPreviousNarrations,
            yourRecordingRequests,
            rssLink: res.rss_url,
            hasActiveMonthlyStandardSub: res.active_monthly_standard_sub,
            showModals: {
              showConfirmArticle: nukeModals ? false : state.showModals.showConfirmArticle,
              showGetMoreCredits: nukeModals ? false : state.showModals.showGetMoreCredits,
              showCashOut: nukeModals ? false : state.showModals.showCashOut,
              showConfirmRequestClaim: nukeModals ? false : state.showModals.showConfirmRequestClaim,
              showUploadRecording: nukeModals ? false : state.showModals.showUploadRecording,
            }
          });
          parrot.getOpenRequestedRecordings(accessToken).then(openRequests => {
            if (openRequests?.length !== 0) {
              const [claimedRequestedRecordings, openRequestedRecordings] = partitionArray(openRequests?.reverse(), request => request.claimed === true);
              setState({
                ...state,
                ...newStateData,
                fetchingDashboardData: false,
                credits: res.credits,
                dollars: res.dollars,
                isListener: res.isListener,
                isNarrator: res.isNarrator,
                yourPreviousNarrations,
                yourRecordingRequests,
                claimedRequestedRecordings,
                openRequestedRecordings,
                otherRequestsFetching: false,
                rssLink: res.rss_url,
                hasActiveMonthlyStandardSub: res.active_monthly_standard_sub,
                showModals: {
                  showConfirmArticle: nukeModals ? false : state.showModals.showConfirmArticle,
                  showGetMoreCredits: nukeModals ? false : state.showModals.showGetMoreCredits,
                  showCashOut: nukeModals ? false : state.showModals.showCashOut,
                  showConfirmRequestClaim: nukeModals ? false : state.showModals.showConfirmRequestClaim,
                  showUploadRecording: nukeModals ? false : state.showModals.showUploadRecording,
                }
              });
            } else {
              setState({
                ...state,
                ...newStateData,
                fetchingDashboardData: false,
                credits: res.credits,
                dollars: res.dollars,
                isListener: res.isListener,
                isNarrator: res.isNarrator,
                yourPreviousNarrations,
                yourRecordingRequests,
                otherRequestsFetching: false,
                rssLink: res.rss_url,
                hasActiveMonthlyStandardSub: res.active_monthly_standard_sub,
                showModals: {
                  showConfirmArticle: nukeModals ? false : state.showModals.showConfirmArticle,
                  showGetMoreCredits: nukeModals ? false : state.showModals.showGetMoreCredits,
                  showCashOut: nukeModals ? false : state.showModals.showCashOut,
                  showConfirmRequestClaim: nukeModals ? false : state.showModals.showConfirmRequestClaim,
                  showUploadRecording: nukeModals ? false : state.showModals.showUploadRecording,
                }
              });
            }
          });
        }
      });
    } catch (err) {
      console.log(err);
    }
  }

  useLayoutEffect(() => {
    props.mixpanel?.track("Dashboard Visit");
    fetchDashboardData();
  }, [history]);

  const toggleGetMoreCredits = () => {
    setState({
      ...state,
      showModals: {
        ...state.showModals,
        showGetMoreCredits: !state.showModals.showGetMoreCredits
      }
    });
  };

  const toggleCashOut = () => {
    setState({
      ...state,
      showModals: {
        ...state.showModals,
        showCashOut: !state.showModals.showCashOut
      }
    });
  };

  const toggleConfirmRequestClaim = (newClaimingRequest) => {
    setState({
      ...state,
      claimingRequest: !state.claimingRequest ? newClaimingRequest : undefined,
      showModals: {
        ...state.showModals,
        showConfirmRequestClaim: !state.showModals.showConfirmRequestClaim
      }
    });
  };

  const toggleUploadRecording = (id) => {
    setState({
      ...state,
      uploadingRecordingId: !state.uploadingRecordingId?.id ? id : undefined,
      showModals: {
        ...state.showModals,
        showUploadRecording: !state.showModals.showUploadRecording
      }
    });
  };

  const toggleShowConfirmArticle = () => {
    if (state.showModals.showConfirmArticle === true) {
      setState({
        ...state,
        pendingRecordingRequestError: false,
        pendingRecordingRequestUrl: undefined,
        pendingRecordingRequest: undefined,
        showModals: {
          ...state.showModals,
          showConfirmArticle: false
        }
      });
    } else {
      setState({
        ...state,
        showModals: {
          ...state.showModals,
          showConfirmArticle: true
        }
      });
    }
  };

  const consumeURL = async (url) => {
    props.mixpanel?.track("Got Article Request Info");
    setState({
      ...state,
      pendingRecordingRequestUrl: url
    });
    const accessToken = await props.auth0.getAccessTokenSilently();
    parrot.getArticleInfo(accessToken, {article_url: url}).then(res => {
      if (res !== undefined) {
        setState({
          ...state,
          showModals: {
            showConfirmArticle: true
          },
          pendingRecordingRequestError: false,
          pendingRecordingRequestUrl: res.url,
          pendingRecordingRequest: {
            title: res.title,
            credits: res.credits
          }
        });
      } else {
        setState({
          ...state,
          pendingRecordingRequestError: true,
          pendingRecordingRequestUrl: undefined,
          pendingRecordingRequest: undefined
        });
      }
    });
  }

  const submitRecordingRequest = async () => {
    props.mixpanel?.track("Submitted Article Request");
    const requestingUrl = state.pendingRecordingRequestUrl;
    setState({
      ...state,
      pendingRecordingRequestUrl: undefined,
      pendingRecordingRequest: undefined
    });
    const accessToken = await props.auth0.getAccessTokenSilently();
    parrot.confirmRequestNarration(accessToken, {article_url: requestingUrl}).then(res => {
      window.location.reload();
    });
  }

  const goToStripe = async () => {
    props.mixpanel?.track("Going to Stripe to Subscribe");
    const accessToken = await props.auth0.getAccessTokenSilently();
    const stripe = await loadStripe(process.env.REACT_APP_STRIPE_KEY);
    internalStripeApi.createCheckoutSession(accessToken).then(res => {
      stripe.redirectToCheckout({
        sessionId: res.sessionId
      })
        .then(response => {
          console.log('all done');
        });
    });
  }

  const confirmCashOut = () => {
    console.log('TODO: confirm cash out');
  }

  const confirmRequestClaim = async () => {
    props.mixpanel?.track("Claimed Article to Narrate");
    const accessToken = await props.auth0.getAccessTokenSilently();
    parrot.claimRequestedRecording(accessToken, state.claimingRequest.recording_id).then(async () => {
      await fetchDashboardData({ claimingRequest: undefined });
    })
  }

  const becomeNarrator = () => {
    console.log('TODO: become narrator');
  }

  const switchTab = (selectedTab) => {
    if (state.tab !== selectedTab) {
      setState({
        ...state,
        tab: selectedTab
      });
    }
  }

  if (state.fetchingDashboardData || props.auth0.isLoading) {
    return <DashboardLoadingState />;
  } else {
    return (
      <ThemeContext.Consumer>
        {themeContext => (
          <div className="background" style={{ backgroundColor: themeContext.theme.body }}>
            <Navbar authed={true} />
            {/* MODALS */}
            <Modal title="Upload recording" onClose={toggleUploadRecording} closeText="Cancel" show={state.showModals.showUploadRecording}>
              <FileUpload id={state.uploadingRecordingId} getToken={props.auth0.getAccessTokenSilently} />
            </Modal>
            <Modal title="Get more credits" onClose={toggleGetMoreCredits} closeText="Cancel" show={state.showModals.showGetMoreCredits}>
              <Type type="h1" style={{ marginBottom: 0 }}>$10.00 / month</Type>
              <Type>Immediately receive 1000 credits per month to request human recordings of Internet content.</Type>
              <br />
              <Button value="Continue" onClick={goToStripe} />
            </Modal>
            <Modal title="Cash out earnings" onClose={toggleCashOut} closeText="Cancel" show={state.showModals.showCashOut}>
              <Type>You will be sent ${state.dollars.toFixed(2)}.</Type>
              <br />
              <Button value="Confirm cash out" onClick={confirmCashOut} />
            </Modal>
            <Modal title="Confirm request claim" onClose={toggleConfirmRequestClaim} closeText="Cancel" show={state.showModals.showConfirmRequestClaim}>
              <Type>Are you sure you want to claim this narration request?</Type>
              <br />
              <div>
                <Type style={{ margin: 0 }}>{state.claimingRequest?.name}</Type>
                <Type>Pay upon approved upload: ${parseFloat(state.claimingRequest?.bounty).toFixed(2)}</Type>
              </div>
              <Type type="caption" style={{ fontWeight: '600' }}>Upload expected within 2 hours</Type>
              <br />
              <Button value="Claim request" onClick={confirmRequestClaim} />
            </Modal>
            <Modal title={state.pendingRecordingRequest?.credit_cost > state.credits ? "Insufficient Credits" : "Confirm recording request"} onClose={toggleShowConfirmArticle} closeText="Cancel" show={state.showModals.showConfirmArticle}>
              {state.pendingRecordingRequest !== undefined ?
                // If you aren't a listener yet you get it for free
                (!state.isListener ?
                  <>
                    <Type>{state.pendingRecordingRequest?.title}</Type>
                    <Type>{state.pendingRecordingRequestUrl}</Type>
                    <div>
                      <Type>Cost: Free</Type>
                    </div>
                    <Button disabled={state.pendingRecordingRequest === undefined} value="Submit" onClick={submitRecordingRequest} />
                  </>
                  :
                  state.pendingRecordingRequest?.credit_cost > state.credits ?
                    <>
                      <Type>Sorry, you don't have enough credits to request a recording of "{state.pendingRecordingRequest?.title}".</Type>
                      <div>
                        <Type>You have: {state.credits} credits</Type>
                        <Type>Cost: {state.pendingRecordingRequest?.credit_cost} credits</Type>
                      </div>
                      <Button value="Get more credits" onClick={() => {
                        setState({
                          ...state,
                          showModals: {
                            showGetMoreCredits: true,
                            showConfirmArticle: false
                          }
                        });
                      }} />
                    </>
                    :
                    <>
                      <Type>{state.pendingRecordingRequest?.title}</Type>
                      <Type>{state.pendingRecordingRequestUrl}</Type>
                      <div>
                        <Type>You have: {state.credits} credits</Type>
                        <Type>Cost: {state.pendingRecordingRequest?.credit_cost} credits</Type>
                      </div>
                      <Button disabled={state.pendingRecordingRequest === undefined} value="Submit" onClick={submitRecordingRequest} />
                    </>
                )
                :
                <div className="modal-loader-div">
                  <Loader style={{ margin: 'auto' }} />
                </div>
              }
            </Modal>
            {/* EVERYTHING ELSE */}
            <div className="dashboard-content-div">
              {state.tab === 'listening' ?
                (state.isListener ?
                  <>
                    <div className="dashboard-top-navigation">
                      <div className="entire-credit-div">
                        <div className="credit-plan-div">
                          <Button onClick={state.hasActiveMonthlyStandardSub ? undefined : toggleGetMoreCredits} type="secondary" value={`${!state.credits ? 0 : state.credits} credits`} style={{ color: !state.credits ? themeContext.theme.danger : themeContext.theme.text, margin: 0, padding: 0 }} />
                          {state.hasActiveMonthlyStandardSub ?
                            <Type type="caption" style={{ margin: 0, padding: 0 }}>Standard Plan</Type>
                            :
                            null
                          }
                        </div>
                        {state.hasActiveMonthlyStandardSub ?
                          null
                          :
                          <Button value="Get more" onClick={toggleGetMoreCredits} />
                        }
                      </div>
                      <div>
                        <Button type="secondary" value="Listening" style={{ color: state.tab === 'listening' ? themeContext.theme.text : themeContext.theme.subtle, textDecoration: 'none' }} onClick={() => switchTab('listening')} />
                        <Button type="secondary" value="Narrating" style={{ color: state.tab === 'narrating' ? themeContext.theme.text : themeContext.theme.subtle, textDecoration: 'none' }} onClick={() => switchTab('narrating')} />
                      </div>
                    </div>
                    <br />
                    <TextSubmit forceErrorState={state.pendingRecordingRequestError} initialValue={state.pendingRecordingRequestUrl} isLoading={state.pendingRecordingRequestUrl !== undefined} placeholder="Enter a link to an article →" type="url" buttonText="Submit" onSubmit={consumeURL} />
                    <Type>Your RSS feed link: {state.rssLink}</Type>
                    <Type type="h2" style={{ margin: '0.5em 0 0.5em 0' }}>Open requests</Type>
                    <YourRecordingRequestRow type="labels" name="Link" status="Status" creditCost="Credit Cost" />
                    {state.yourRecordingRequests.map((requestedRecording, index) => {
                      const status = requestedRecording.narration ? 'Fulfilled' : 'Processing';
                      if (status === 'Processing') {
                        return (
                          <YourRecordingRequestRow key={index} contentId={requestedRecording.content_id} name={requestedRecording.content_url} status={status} creditCost={requestedRecording.credit_cost} />
                        );
                      }
                    })}
                    <br />
                    <Type type="h2" style={{ margin: '0.5em 0 0.5em 0' }}>Completed requests</Type>
                    <YourRecordingRequestRow type="labels" name="Link" status="Fulfilled" creditCost="Credit Cost" />
                    {state.yourRecordingRequests.map((requestedRecording, index) => {
                      const status = requestedRecording.narration ? 'Fulfilled' : 'Processing';
                      if (status === 'Fulfilled') {
                        return (
                          <YourRecordingRequestRow key={index} contentId={requestedRecording.content_id} name={requestedRecording.content_url} status={status} creditCost={requestedRecording.credit_cost} />
                        );
                      }
                    })}
                  </>
                  :
                  <>
                    <div style={{ alignSelf: 'flex-end' }}>
                      <Button type="secondary" value="Listening" style={{ color: state.tab === 'listening' ? themeContext.theme.text : themeContext.theme.subtle, textDecoration: 'none' }} onClick={() => switchTab('listening')} />
                      <Button type="secondary" value="Narrating" style={{ color: state.tab === 'narrating' ? themeContext.theme.text : themeContext.theme.subtle, textDecoration: 'none' }} onClick={() => switchTab('narrating')} />
                    </div>
                    <Type type="h1" style={{ margin: '1em 0 0.0 0' }}>Request a narration.</Type>
                    <Type>Your first request is 100% free. No credit card required.</Type>
                    <br />
                    <TextSubmit forceErrorState={state.pendingRecordingRequestError} initialValue={state.pendingRecordingRequestUrl} isLoading={state.pendingRecordingRequestUrl !== undefined} placeholder="Enter a link to an article →" type="url" buttonText="Submit" onSubmit={consumeURL} />
                  </>
                )
                :
                (state.isNarrator ?
                  <>
                    <div className="dashboard-top-navigation">
                      <div>
                        <Button type="secondary" onClick={!!state.dollars ? toggleCashOut : undefined} value={`$${state.dollars.toFixed(2)} earned`} />
                        {!!state.dollars ?
                          <Button value="Cash out" onClick={toggleCashOut} />
                          :
                          null
                        }
                      </div>
                      <div>
                        <Button type="secondary" value="Listening" style={{ color: state.tab === 'listening' ? themeContext.theme.text : themeContext.theme.subtle, textDecoration: 'none' }} onClick={() => switchTab('listening')} />
                        <Button type="secondary" value="Narrating" style={{ color: state.tab === 'narrating' ? themeContext.theme.text : themeContext.theme.subtle, textDecoration: 'none' }} onClick={() => switchTab('narrating')} />
                      </div>
                    </div>
                    {state.otherRequestsFetching ?
                      <>
                        <OpenRecordingRequestRow type="labels" name="Link" bounty="Pay" />
                        <div className="modal-loader-div">
                          <Loader />
                        </div>
                      </>
                      :
                      <>
                        <Type type="h1" style={{ margin: '1em 0 0.5em 0' }}>Your claimed requests</Type>
                        {state.claimedRequestedRecordings?.length !== 0 ?
                          <>
                            <ClaimedRecordingRequestRow type="labels" name="Link" bounty="Pay" />
                            {state.claimedRequestedRecordings?.map((claimedRequestedRecordings, index) => {
                              return (
                                <ClaimedRecordingRequestRow key={index} recording_id={claimedRequestedRecordings.id} name={claimedRequestedRecordings.content_url} bounty={claimedRequestedRecordings.cost} onUploadClick={toggleUploadRecording} />
                              );
                            })}
                          </>
                          :
                          <div className="modal-loader-div">
                            <Type type="caption" style={{ margin: 0, padding: 0 }}>Claim then upload requested narrations to get paid.</Type>
                          </div>
                        }
                        <Type type="h1" style={{ margin: '1em 0 0.5em 0' }}>Open requests</Type>
                        {state.openRequestedRecordings?.length !== 0 ?
                          <>
                            <OpenRecordingRequestRow type="labels" name="Link" bounty="Pay" />
                            {state.openRequestedRecordings?.map((requestedRecording, index) => {
                              return (
                                <OpenRecordingRequestRow key={index} recording_id={requestedRecording.id} name={requestedRecording.content_url} bounty={requestedRecording.cost} onClaimClick={toggleConfirmRequestClaim} />
                              );
                            })}
                          </>
                          :
                          <div className="modal-loader-div">
                            <Type type="caption">There are no open recording requests.</Type>
                          </div>
                        }
                      </>
                    }
                  </>
                  :
                  <>
                    <div style={{ alignSelf: 'flex-end' }}>
                      <Button type="secondary" value="Listening" style={{ color: state.tab === 'listening' ? themeContext.theme.text : themeContext.theme.subtle, textDecoration: 'none' }} onClick={() => switchTab('listening')} />
                      <Button type="secondary" value="Narrating" style={{ color: state.tab === 'narrating' ? themeContext.theme.text : themeContext.theme.subtle, textDecoration: 'none' }} onClick={() => switchTab('narrating')} />
                    </div>
                    <Type type="h1" style={{ margin: '1em 0 0 0' }}>(Coming soon) Become a Narrator.</Type>
                    <Type>Earn cash by recording narrations of articles requested by ReadMeThis users.</Type>
                    <br />
                    <Button disabled value="Apply now" onClick={becomeNarrator} />
                  </>
                )
              }
            </div>
          </div>
        )}
      </ThemeContext.Consumer>
    );
  }
}

export default withAuthenticationRequired(withAuth0(Dashboard), {
  // Show a message while the user waits to be redirected to the login page.
  onRedirecting: () => (
    <DashboardLoadingState />
  )
});
